import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';
import MmSelect from './MmSelect';
import { downloadTextFile } from '../utils/importTemplates';
import {
  buildContributionPeriodReport,
  buildContributionPeriodReportCsv,
  defaultPeriodSelection,
  resolveContributionPeriodRange,
} from '../utils/contributionPeriodReport';

/**
 * Rapport périodique cotisations (vue staff / président).
 */
export default function ContributionPeriodReportModal({
  open,
  onClose,
  contributions = [],
  members = [],
  getContributionMemberId,
  getContributionMemberName,
  getMonthName,
  orgName = '',
  localeFmt = 'fr-FR',
  initialPersonId = '',
}) {
  const { t } = useTranslation('common');
  const defaults = defaultPeriodSelection();
  const [periodType, setPeriodType] = useState(defaults.periodType);
  const [year, setYear] = useState(defaults.year);
  const [month, setMonth] = useState(defaults.month);
  const [quarter, setQuarter] = useState(defaults.quarter);
  const [half, setHalf] = useState(defaults.half);
  const [personId, setPersonId] = useState(String(initialPersonId || '').trim() || 'all');

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => y - i);
  }, []);

  const memberOptions = useMemo(() => {
    const opts = [
      { value: 'all', label: t('contributionsPage.report.allMembers') },
    ];
    for (const m of members || []) {
      const id = String(m?.id || '').trim();
      if (!id) continue;
      opts.push({
        value: id,
        label: String(m?.name || id).trim() || id,
      });
    }
    return opts;
  }, [members, t]);

  const range = useMemo(
    () => resolveContributionPeriodRange(periodType, { year, month, quarter, half }),
    [periodType, year, month, quarter, half]
  );

  const periodLabel = useMemo(() => {
    if (!range) return '';
    if (range.labelKey === 'monthly' && typeof getMonthName === 'function') {
      return getMonthName(range.startYm);
    }
    const key = `contributionsPage.report.periodLabel.${range.labelKey}`;
    return t(key, range.labelParams);
  }, [range, t, getMonthName]);

  const report = useMemo(() => {
    if (!range) return { rows: [], summary: { count: 0, totalAmount: 0 }, startYm: '', endYm: '' };
    return buildContributionPeriodReport(contributions, range, {
      personId: personId === 'all' ? '' : personId,
      getMemberId: getContributionMemberId,
      getMemberName: getContributionMemberName,
    });
  }, [contributions, range, personId, getContributionMemberId, getContributionMemberName]);

  if (!open) return null;

  const statusLabel = (status) => {
    if (status === 'completed') return t('contributionsPage.chips.completed');
    if (status === 'pending') return t('contributionsPage.chips.pending');
    if (status === 'rejected') return t('contributionsPage.chips.rejected');
    return status || '—';
  };

  const handleDownloadCsv = () => {
    if (!range) return;
    const csv = buildContributionPeriodReportCsv(report, {
      orgName,
      periodLabel,
      localeFmt,
    });
    const stamp = `${range.startYm}_${range.endYm}`.replace(/-/g, '');
    downloadTextFile(`rapport_cotisations_${periodType}_${stamp}.csv`, csv);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <ModalShell
      maxWidthClass="max-w-4xl"
      panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
      onBackdropClick={onClose}
    >
      <div className="mm-modal-header print:hidden">
        <div className="min-w-0">
          <p className="mm-hero__label text-[#1f6ab8]">{t('contributionsPage.report.kicker')}</p>
          <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
            {t('contributionsPage.report.title')}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('contributionsPage.form.closeAria')}
          className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="space-y-3 overflow-y-auto px-4 py-3 print:overflow-visible print:px-0">
          <div className="grid gap-3 sm:grid-cols-2 print:hidden">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('contributionsPage.report.periodType')}
              </label>
              <MmSelect
                value={periodType}
                onChange={setPeriodType}
                options={[
                  { value: 'monthly', label: t('contributionsPage.report.types.monthly') },
                  { value: 'quarterly', label: t('contributionsPage.report.types.quarterly') },
                  { value: 'semiannual', label: t('contributionsPage.report.types.semiannual') },
                  { value: 'annual', label: t('contributionsPage.report.types.annual') },
                ]}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('contributionsPage.report.year')}
              </label>
              <MmSelect
                value={String(year)}
                onChange={(v) => setYear(Number(v))}
                options={yearOptions.map((y) => ({ value: String(y), label: String(y) }))}
              />
            </div>
            {periodType === 'monthly' ? (
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('contributionsPage.report.month')}
                </label>
                <MmSelect
                  value={String(month)}
                  onChange={(v) => setMonth(Number(v))}
                  options={Array.from({ length: 12 }, (_, i) => {
                    const m = i + 1;
                    const label = new Date(year, i, 1).toLocaleDateString(localeFmt, { month: 'long' });
                    return { value: String(m), label };
                  })}
                />
              </div>
            ) : null}
            {periodType === 'quarterly' ? (
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('contributionsPage.report.quarter')}
                </label>
                <MmSelect
                  value={String(quarter)}
                  onChange={(v) => setQuarter(Number(v))}
                  options={[1, 2, 3, 4].map((q) => ({
                    value: String(q),
                    label: t('contributionsPage.report.quarterOption', { quarter: q }),
                  }))}
                />
              </div>
            ) : null}
            {periodType === 'semiannual' ? (
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('contributionsPage.report.half')}
                </label>
                <MmSelect
                  value={String(half)}
                  onChange={(v) => setHalf(Number(v))}
                  options={[
                    { value: '1', label: t('contributionsPage.report.halfOption', { half: 1 }) },
                    { value: '2', label: t('contributionsPage.report.halfOption', { half: 2 }) },
                  ]}
                />
              </div>
            ) : null}
            <div className={periodType === 'annual' ? 'sm:col-span-2' : ''}>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('contributionsPage.report.member')}
              </label>
              <MmSelect
                value={personId}
                onChange={setPersonId}
                searchable={memberOptions.length > 8}
                options={memberOptions}
              />
            </div>
          </div>

          <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200 print:bg-white print:ring-0">
            <p className="font-display text-sm font-bold text-[#0f3350]">
              {orgName ? `${orgName} · ` : ''}
              {periodLabel}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {range ? `${getMonthName?.(range.startYm) || range.startYm} → ${getMonthName?.(range.endYm) || range.endYm}` : '—'}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-500">{t('contributionsPage.report.kpi.total')}</p>
                <p className="text-sm font-bold tabular-nums text-[#0f3350]">
                  {Number(report.summary.totalAmount || 0).toLocaleString(localeFmt)} FBU
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-500">{t('contributionsPage.report.kpi.paid')}</p>
                <p className="text-sm font-bold tabular-nums text-emerald-800">
                  {report.summary.completedCount} · {Number(report.summary.completedAmount || 0).toLocaleString(localeFmt)}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-500">{t('contributionsPage.report.kpi.pending')}</p>
                <p className="text-sm font-bold tabular-nums text-amber-800">
                  {report.summary.pendingCount} · {Number(report.summary.pendingAmount || 0).toLocaleString(localeFmt)}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-500">{t('contributionsPage.report.kpi.members')}</p>
                <p className="text-sm font-bold tabular-nums text-[#0f3350]">{report.summary.membersCount || 0}</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-semibold">{t('contributionsPage.report.cols.member')}</th>
                  <th className="px-3 py-2 font-semibold">{t('contributionsPage.report.cols.month')}</th>
                  <th className="px-3 py-2 font-semibold">{t('contributionsPage.report.cols.amount')}</th>
                  <th className="px-3 py-2 font-semibold">{t('contributionsPage.report.cols.status')}</th>
                  <th className="px-3 py-2 font-semibold">{t('contributionsPage.report.cols.method')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {report.rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                      {t('contributionsPage.report.empty')}
                    </td>
                  </tr>
                ) : (
                  report.rows.map((r) => (
                    <tr key={r.id || `${r.memberId}-${r.month}-${r.amount}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.memberName}</td>
                      <td className="px-3 py-2 capitalize text-slate-700">
                        {getMonthName?.(r.month) || r.month}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-800">
                        {Number(r.amount || 0).toLocaleString(localeFmt)}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{statusLabel(r.status)}</td>
                      <td className="px-3 py-2 text-slate-600">{r.paymentMethod || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mm-modal-footer flex flex-wrap gap-2 print:hidden">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] flex-1 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 sm:flex-none"
          >
            {t('contributionsPage.form.cancel')}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={!report.rows.length}
            className="min-h-[44px] flex-1 rounded-xl border border-slate-300 px-4 text-sm font-semibold text-slate-800 disabled:opacity-50 sm:flex-none"
          >
            {t('contributionsPage.report.print')}
          </button>
          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={!report.rows.length}
            className="mm-cta min-h-[44px] flex-1 px-4 text-sm disabled:opacity-50 sm:flex-none"
          >
            {t('contributionsPage.report.downloadCsv')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
